import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, FileText, Wrench, Users, CalendarClock, MapPinned,
  AlertTriangle, ArrowRight, Calculator, Lightbulb,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, fmtDate, statusLabel, statusColor } from "@/lib/format";
import { DAILY_TIPS } from "@/pages/dailyTips";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value, sub, tone = "text-slate-900", testid }) {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-mono-num uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
          <Icon className="h-4 w-4 text-slate-400" />
        </div>
        <p className={`mt-2 font-mono-num text-2xl font-semibold ${tone}`}>{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [costs, setCosts] = useState(null);
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  const tip = DAILY_TIPS[dayOfYear % DAILY_TIPS.length];

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/costs/structure").then((r) => setCosts(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="py-20 text-center text-slate-400" data-testid="dashboard-loading">Carregando...</div>;

  return (
    <div className="space-y-6 animate-in-up" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Olá, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500">Visão geral da sua operação</p>
        </div>
        <Link to="/app/calculadora">
          <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white active:scale-[0.98] transition-transform" data-testid="new-quote-btn">
            <Calculator className="h-4 w-4 mr-2" /> Novo orçamento
          </Button>
        </Link>
      </div>

      <Card className="border-sky-200 bg-sky-50 shadow-sm" data-testid="daily-tip-card">
        <CardContent className="p-4 flex gap-3 items-start">
          <div className="bg-sky-600 rounded-full p-2 shrink-0">
            <Lightbulb className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">GEPR Diário · {tip.cat}</p>
            <p className="text-sm text-slate-700 mt-0.5">{tip.text}</p>
          </div>
        </CardContent>
      </Card>

      {costs && (
        <Card className={`shadow-sm ${costs.minimal_done ? "border-slate-200" : "border-amber-300 bg-amber-50"}`} data-testid="costs-progress-card">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">
                {costs.minimal_done ? `Sua configuração financeira está ${costs.completion}% completa` : "Configure seus custos operacionais"}
              </p>
              <p className="text-xs text-slate-500">
                {costs.minimal_done
                  ? (costs.completion < 100 ? `Aprofunde: ${(costs.indicators?.missing || []).slice(0, 2).join(" · ") || "—"}` : "Estrutura completa — precificação individualizada ativa.")
                  : "Conheça o custo da sua operação antes de definir seu preço."}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {costs.minimal_done && (
                <div className="w-28 bg-slate-200 rounded-full h-2">
                  <div className="bg-sky-600 h-2 rounded-full" style={{ width: `${costs.completion}%` }} />
                </div>
              )}
              <Link to="/app/custos">
                <Button size="sm" variant={costs.minimal_done ? "outline" : "default"} className={costs.minimal_done ? "" : "bg-amber-600 hover:bg-amber-700 text-white"} data-testid="costs-progress-cta">
                  {costs.minimal_done ? "Detalhar" : "Preencher agora"}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={TrendingUp} label="Faturamento" value={brl(d.faturamento)} sub="orçamentos aprovados" testid="stat-faturamento" />
        <Stat icon={TrendingUp} label="Lucro estimado" value={brl(d.lucro_estimado)} tone="text-green-600" sub={`margem média ${d.margem_media}%`} testid="stat-lucro" />
        <Stat icon={FileText} label="Orçamentos" value={d.orcamentos_total} sub={`${d.orcamentos_aprovados} aprovados · ${d.orcamentos_enviados} enviados`} testid="stat-orcamentos" />
        <Stat icon={Wrench} label="OS" value={d.os_andamento + d.os_agendadas} sub={`${d.os_concluidas} concluídas`} testid="stat-os" />
        <Stat icon={Users} label="Clientes" value={d.clientes} sub={`${d.equipamentos} equipamentos`} testid="stat-clientes" />
        <Stat icon={FileText} label="Ticket médio" value={brl(d.ticket_medio)} testid="stat-ticket" />
        <Stat icon={CalendarClock} label="Manutenções" value={d.manutencoes_atrasadas.length + d.manutencoes_proximas.length} tone={d.manutencoes_atrasadas.length ? "text-red-600" : "text-slate-900"} sub={`${d.manutencoes_atrasadas.length} atrasadas`} testid="stat-manutencoes" />
        <Stat icon={MapPinned} label="Contribuições" value={d.contribuicoes} sub="na base colaborativa" testid="stat-contribuicoes" />
      </div>

      {d.manutencoes_atrasadas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3" data-testid="maintenance-alert">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 text-sm">Manutenções atrasadas</p>
            <p className="text-sm text-red-700">
              {d.manutencoes_atrasadas.length} manutenção(ões) preventivas vencidas.{" "}
              <Link to="/app/manutencao" className="underline font-medium">Ver agora</Link>
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Últimos orçamentos</h2>
              <Link to="/app/orcamentos" className="text-sm text-sky-700 hover:underline flex items-center gap-1" data-testid="see-quotes-link">
                Ver todos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            {d.ultimos_orcamentos.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum orçamento ainda. Crie o primeiro!</p>
            ) : (
              <div className="space-y-2">
                {d.ultimos_orcamentos.map((q) => (
                  <Link key={q.id} to={`/app/orcamentos/${q.id}`} className="flex items-center justify-between border border-slate-100 rounded-lg p-3 hover:bg-slate-50 transition-colors" data-testid={`dash-quote-${q.number}`}>
                    <div>
                      <p className="text-sm font-medium">{q.number} · {q.service_type}</p>
                      <p className="text-xs text-slate-500">{fmtDate(q.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono-num text-sm font-semibold">{brl(q.calc?.preco_final)}</p>
                      <Badge variant="outline" className={`text-[10px] ${statusColor[q.status]}`}>{statusLabel[q.status]}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Próximas ordens de serviço</h2>
              <Link to="/app/os" className="text-sm text-sky-700 hover:underline flex items-center gap-1" data-testid="see-os-link">
                Ver todas <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            {d.proximas_os.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma OS agendada.</p>
            ) : (
              <div className="space-y-2">
                {d.proximas_os.map((o) => (
                  <div key={o.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-3" data-testid={`dash-os-${o.number}`}>
                    <div>
                      <p className="text-sm font-medium">{o.number}</p>
                      <p className="text-xs text-slate-500">{o.problem || o.service_done || "Serviço"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{fmtDate(o.scheduled_date)}</p>
                      <Badge variant="outline" className={`text-[10px] ${statusColor[o.status]}`}>{statusLabel[o.status]}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {d.referencias_usadas.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="refs-used-card">
          <CardContent className="p-5">
            <h2 className="font-semibold mb-3">Referências regionais usadas recentemente</h2>
            <div className="space-y-2">
              {d.referencias_usadas.map((r, i) => (
                <p key={i} className="text-sm text-slate-600">
                  {r.category_name || r.category}: <span className="font-mono-num font-medium">{r.range_label}</span>
                  <span className="text-slate-400"> · {r.count} contribuições · {r.updated_label}</span>
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
